import { DownOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import { convertToRgb } from '@carboncopyco/utils';
import { useCallback, useRef, useState } from 'react';
import { ChromePointer } from 'react-color/es/components/chrome/ChromePointer';
import { ChromePointerCircle } from 'react-color/es/components/chrome/ChromePointerCircle';
import { Alpha, ColorWrap, Hue } from 'react-color/es/components/common';
import { isValidHex } from 'react-color/es/helpers/color';
import { ColorHex } from '../../constants';
import { Dropdown, DropdownItem } from '../Dropdown';
import { TextCaption } from '../Typography/TextCaption';
import { ColorInput } from './ColorInput';
import Saturation from './Saturation';

export type TColorType = 'rgb' | 'hsl' | 'hex';
export type TColorOption = {
  value: TColorType;
  label: string;
};
export interface ColorPickerProps {
  color?: string;
  alpha?: number;
  onChange: (color: any, e: MouseEvent) => void;
  onChangeComplete?: (color: any, e: MouseEvent) => void;
  displayAlpha?: boolean;
  availableColorTypes?: TColorType[];
}

function normalizeColor({
  color,
  alpha,
}: {
  color?: string | Record<string, any>;
  alpha?: number;
}) {
  if (typeof color === 'string' && isValidHex(color)) {
    const rgb = convertToRgb(color);

    return {
      ...rgb,
      a: alpha ?? rgb.a,
    };
  }

  return color;
}

const NormalizeColorWrap = (Component) => {
  // allows to use hex colors in the picker.
  return ({ ...props }: ColorPickerProps) => {
    const { color, alpha } = props;

    return (
      <Component
        {...props}
        color={normalizeColor({ color, alpha })}
      />
    );
  };
};

const defaultColorsOptions: Record<TColorType, TColorOption> = {
  rgb: { value: 'rgb', label: 'RGB' },
  hsl: { value: 'hsl', label: 'HSL' },
  hex: { value: 'hex', label: 'HEX' },
};

const getColorOptions = (availableColorTypes: TColorType[]): TColorOption[] => {
  if (!availableColorTypes) {
    return Object.values(defaultColorsOptions);
  }
  const colorOptions: TColorOption[] = [];
  availableColorTypes.forEach((type) => {
    if (defaultColorsOptions[type]) {
      colorOptions.push(defaultColorsOptions[type]);
    }
  });

  return colorOptions;
};

export const ColorPicker = NormalizeColorWrap(
  ColorWrap(
    ({
      onChange,
      rgb,
      hsl,
      hsv,
      hex,
      renderers,
      availableColorTypes,
      displayAlpha = true,
    }) => {
      const options = getColorOptions(availableColorTypes);
      const [colorType, setColorType] = useState<TColorType>(options[0].value);
      const [visible, setVisible] = useState<boolean>(false);
      const dropdownRef = useRef<HTMLDivElement>(null);

      const getFields = useCallback(() => {
        switch (colorType) {
          case 'hex':
            return (
              <ColorInput
                label={colorType}
                value={hex}
                rgb={rgb}
                hsl={hsl}
                onChange={onChange}
              />
            );
          case 'hsl':
          case 'rgb': {
            const colorValue = colorType === 'rgb' ? rgb : hsl;
            if (!displayAlpha && colorValue.a) {
              delete colorValue.a;
            }
            return Object.keys(colorValue).map((key) => {
              return (
                <ColorInput
                  rgb={rgb}
                  hsl={hsl}
                  key={key}
                  label={key}
                  value={colorValue[key]}
                  onChange={onChange}
                />
              );
            });
          }
          default:
            return rgb;
        }
      }, [rgb, hsl, hsv, hex, colorType]);

      return (
        <div
          className="w-ds-66 bg-ds-accent3-100"
          data-ds-color-picker="true"
        >
          <div
            className="w-full relative rounded-md overflow-hidden"
            style={{
              paddingBottom: '55%',
            }}
          >
            <Saturation
              hsl={hsl}
              hsv={hsv}
              pointer={ChromePointerCircle}
              onChange={onChange}
            />
          </div>
          <div className="mt-2.5">
            <TextCaption
              label="Type"
              color="text-ds-natural-300"
            />
            <div className="flex space-x-4">
              <div
                className="w-24"
                ref={dropdownRef}
              >
                <Dropdown
                  container={dropdownRef.current}
                  open={visible}
                  onOpenChange={() => setVisible(!visible)}
                  triggerElement={
                    <div className="flex w-24 justify-between items-center border border-solid rounded-md focus:border-ds-primary border-ds-natural-100 hover:border-ds-natural-150 pl-3 pr-1 py-1.5 text-ds-400 text-ds-natural-300 cursor-pointer">
                      {colorType.toUpperCase()}
                      <DownOutlineIcon />
                    </div>
                  }
                >
                  <div className="w-22">
                    {options.map((el) => (
                      <div
                        className={
                          colorType === el.value
                            ? 'rounded bg-ds-natural-50'
                            : ''
                        }
                        data-edit-tool="true"
                        key={el.value}
                      >
                        <DropdownItem
                          label={el.label}
                          onClick={() => {
                            setColorType(el.value);
                          }}
                          iconRight={
                            colorType === el.value ? (
                              <VOutlineIcon color={ColorHex.natural300} />
                            ) : undefined
                          }
                        />
                      </div>
                    ))}
                  </div>
                </Dropdown>
              </div>
              <div className="relative flex-grow space-y-2.5">
                <div className="relative w-full h-2.5 rounded-md">
                  <Hue
                    hsl={hsl}
                    hex={hex}
                    pointer={ChromePointer}
                    onChange={onChange}
                    style={{
                      radius: '6px',
                      borderRadius: '6px',
                    }}
                  />
                </div>
                <div className="relative w-full h-2.5 rounded-md">
                  {displayAlpha && (
                    <Alpha
                      style={{
                        radius: '6px',
                      }}
                      hex={hex}
                      rgb={rgb}
                      hsl={hsl}
                      pointer={ChromePointer}
                      renderers={renderers}
                      onChange={onChange}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex w-full gap-1.5 items-center mb-2">
              {getFields()}
            </div>
          </div>
        </div>
      );
    }
  )
);
